import React, { useState, useEffect, useRef } from 'react';
import ExportToPDF, {
  ExportToPDFFnType,
} from '../../../../../../../../../Components/ExportToPDF/ExportToPDF';
import {
  IFakturaBildPDF,
  IEkonomiskAnalysVerifikationPDF,
  IEKonomiskAnalysBokfOrderUnderlagPDF,
} from '../../../../../../../../../Components/ExportToPDF/ExportToPDFArgumentTypes';
import { printFakturabild } from '../../verifikationUtils';
import Dialog from '../../../../../../../../../BaseComponents/Dialog/EDDialog';
import EDOverlay from '../../../../../../../../../BaseComponents/Overlay/EDOverlay';
import { makeStyles } from 'tss-react/mui';
import EkonomiskAnalysVerifikationExportSettings from '../EkonomiskAnalysVerifikationExportSettings/EkonomiskAnalysVerifikationExportSettings';
import {
  IUnderlag,
  IVerifikation,
} from '../../Queries/VerifikationQuery/VerifikationQuery';
import { isImageExtension } from '../../../../../../../../../Components/Documents/Documents';
import EDAvenyProgress from '../../../../../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';

interface IProps {
  open: boolean;
  onClose: () => void;
  dbName: string;
  verifikation: IVerifikation;
  hasFakturaBild: boolean;
}

const useStyles = makeStyles()({
  savingOverlay: {
    right: 'calc(50% - 20px)',
    bottom: 'calc(50% - 20px)',
  },
});

export const canPrintUnderlag = (
  underlag: IUnderlag[] | undefined
): boolean => {
  return (
    underlag != null &&
    underlag.length !== 0 &&
    underlag.every(underlag => isImageExtension(underlag.extension))
  );
};

/**
 * En dialog för utskrivning av verifikationer
 */
const EkonomiskAnalysVerifikationPrintPDFModal: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const { onClose } = props;
  const [loading, setLoading] = useState(false);
  const [printVerifikation, setPrintVerifikation] = useState(true);
  const [printFakturabilder, setPrintFakturabilder] = useState(
    props.hasFakturaBild
  );
  const [printUnderlag, setPrintUnderlag] = useState(
    canPrintUnderlag(props.verifikation.underlag)
  );

  const prevLoading = useRef(loading);

  useEffect(() => {
    if (loading === false && prevLoading.current === true) {
      onClose();
    }

    prevLoading.current = loading;
  }, [loading, onClose]);

  const { classes } = useStyles();

  const print = (
    exportFakturaImageToPDF: ExportToPDFFnType<IFakturaBildPDF>,
    exportVerifikationToPDF: ExportToPDFFnType<IEkonomiskAnalysVerifikationPDF>,
    exportBokfUnderlagToPDF: ExportToPDFFnType<IEKonomiskAnalysBokfOrderUnderlagPDF>,
    fakturaBilder: string[],
    verifikationsNr: string,
    xmlFaktura: boolean,
    dbName: string,
    bokfDatum: string,
    journalNr: string,
    verifikation: IVerifikation,
    skannadSvefaktura: boolean
  ) => {
    if (printFakturabilder) {
      printFakturabild(
        exportFakturaImageToPDF,
        fakturaBilder,
        verifikationsNr,
        xmlFaktura,
        dbName,
        bokfDatum,
        journalNr,
        skannadSvefaktura,
        setLoading
      );
    }
    if (printVerifikation) {
      exportVerifikationToPDF(
        {
          reportName: 'Verifikation',
          dbname: dbName,
          verifikationsInfo: verifikation,
          fileName: 'verifikation_' + verifikationsNr,
        },
        setLoading
      );
    }
    if (printUnderlag) {
      exportBokfUnderlagToPDF(
        {
          reportName: 'VerifikationsUnderlag',
          dbname: dbName,
          bokfDatum,
          journalNr,
          verNr: verifikationsNr,
          fileName: 'underlag_' + verifikationsNr,
          levFakturaUnderlagId: props.verifikation.underlag?.filter(
            u => u.source === 'levFakturaAttest'
          )[0]?.id, // Behöver 1 id för att hitta rätt fakturanr och bokfgruppid.
        },
        setLoading
      );
    }
  };

  return (
    <ExportToPDF<IFakturaBildPDF>>
      {exportFakturaBildToPDF => (
        <ExportToPDF<IEkonomiskAnalysVerifikationPDF>>
          {exportVerifikationToPDF => (
            <ExportToPDF<IEKonomiskAnalysBokfOrderUnderlagPDF>>
              {exportBokfUnderlagToPDF => (
                <Dialog
                  open={props.open}
                  onClose={props.onClose}
                  title="Skriv ut"
                  footerActions={[
                    {
                      label: 'Avbryt',
                      onClick: props.onClose,
                    },
                    {
                      label: loading ? 'Skriver ut' : 'Skriv ut',
                      onClick: () => {
                        print(
                          exportFakturaBildToPDF,
                          exportVerifikationToPDF,
                          exportBokfUnderlagToPDF,
                          props.verifikation.extendedInfo
                            ? props.verifikation.extendedInfo.fakturaBilder
                                .value
                            : [],
                          props.verifikation.standardVerNr ??
                            props.verifikation.verNr.toString(),
                          props.verifikation.extendedInfo
                            ? props.verifikation.extendedInfo.xmlFaktura
                                ?.value || false
                            : false,
                          props.dbName,
                          props.verifikation.info?.bokforingsDatum.value || '',
                          props.verifikation.info?.journalNummer.value || '',
                          props.verifikation,
                          props.verifikation.extendedInfo?.skannadSvefaktura
                            ?.value || false
                        );
                      },
                      buttonProps: {
                        disabled:
                          loading ||
                          (!printVerifikation &&
                            !printFakturabilder &&
                            !printUnderlag),
                        disabledReason: loading
                          ? 'Laddar'
                          : !printVerifikation &&
                            !printFakturabilder &&
                            !printUnderlag
                          ? 'Inget valt att skrivas ut'
                          : undefined,
                      },
                    },
                  ]}
                >
                  <EDOverlay
                    overlay={loading && <EDAvenyProgress />}
                    classes={{ overlay: classes.savingOverlay }}
                  >
                    <EkonomiskAnalysVerifikationExportSettings
                      printVerifikation={printVerifikation}
                      printFakturabilder={printFakturabilder}
                      printUnderlag={printUnderlag}
                      setPrintVerifikation={setPrintVerifikation}
                      setPrintFakturabilder={setPrintFakturabilder}
                      setPrintUnderlag={setPrintUnderlag}
                      hasFakturaBild={props.hasFakturaBild}
                      hasUnderlag={canPrintUnderlag(
                        props.verifikation.underlag
                      )}
                    />
                  </EDOverlay>
                </Dialog>
              )}
            </ExportToPDF>
          )}
        </ExportToPDF>
      )}
    </ExportToPDF>
  );
};

export default EkonomiskAnalysVerifikationPrintPDFModal;
